import { Alignment, ArrayUtil, Clipboard, HorizontalAlignment, StringUtil, ValueDisplayValue } from "@mcleod/core";
import { Button, ButtonVariant, Component, ComponentProps, Label, Panel, TooltipOptions } from "@mcleod/components";
import { ImageName } from "@mcleod/images";
import { FinalizedExample } from "./FinalizedExample";

/**
 * Defines the components rendered as part of a single live example.
 */
export class LiveExampleComponents {
    private exampleInput: FinalizedExample;
    labelDescr: Label;
    panelMain: Panel;
    labelCodeCaption: Label;
    buttonCopy: Button;
    labelCode: Label;
    labelResult: Label;
    livePanel: Panel;
    liveComponent: Component;

    constructor(exampleInput: FinalizedExample) {
        this.exampleInput = exampleInput;
    }

    get(): Component[] {
        // Description label may not have been created
        return [ this.labelDescr, this.panelMain ].filter(component => component != null);
    }

    build(): LiveExampleComponents {
        this.buildDescrLabel();
        this.buildCodeSnippet();
        this.buildResultLabel();
        this.buildLivePanel();
        this.panelMain = new Panel({ fillRow: true })
        this.panelMain.add(this.labelCodeCaption, this.buttonCopy, this.labelCode, this.labelResult, this.livePanel);
        this.callOnCreateCallbacks();
        return this;
    }

    private buildDescrLabel() {
        if (this.exampleInput.description != null)
            this.labelDescr = new Label({ text: this.exampleInput.description });
    }

    private buildResultLabel() {
        this.labelResult = new Label({ text: "Result", fontBold: true, marginTop: 8 });
    }
    
    private buildCodeSnippet() {
        const code = this.buildCodeString(this.exampleInput.componentName);
        this.labelCodeCaption = new Label({ text: "Code", color: "default", fontBold: true, rowBreak: false });
        this.buttonCopy = new Button({ variant: ButtonVariant.round, imageName: ImageName.clipboard });
        this.buttonCopy.addClickListener(() => {
            Clipboard.copyText(code);
            const tooltipOptions: TooltipOptions = { shaking: true, timeout: 1200, position: Alignment.RIGHT };
            this.buttonCopy.showAdhocTooltip("Code copied to clipboard", tooltipOptions);
        });
        this.labelCode = new Label({
            text: code,
            color: "subtle.darker",
            backgroundColor: "background4",
            fontFamily: "courier, monospace",
            borderWidth: 1,
            borderRadius: 4,
            padding: 12,
            borderShadow: true,
            fillRow: true,
            marginRight: 16,
            marginLeft: 16
        });
    }

    private buildCodeString(componentName: string): string {
        const varName = componentName.toLowerCase();
        let code = "const " + varName + " = new " + componentName + "(" +
            this.getPropsSpecifier(this.exampleInput.props) + ");\n";
        if (this.exampleInput.value !== undefined) {
            code += varName + "." + this.exampleInput.name + " = " +
                this.doubleQuoteIfString(this.exampleInput.value) + ";"
        }
        return code;
    }

    private getPropsSpecifier(props: Partial<ComponentProps>) {
        if (props != null && Object.keys(props).length > 0) {
            const propStrings: string[] = [];
            Object.entries(props).forEach(([propKey, propValue]) => {
                let finalizedValue: any;
                if (propValue instanceof ValueDisplayValue ||
                    (Array.isArray(propValue) && ArrayUtil.getFirstElement(propValue) instanceof ValueDisplayValue)) {
                    finalizedValue = JSON.stringify(propValue);
                }
                else
                    finalizedValue = this.doubleQuoteIfString(propValue);
                propStrings.push(propKey + ": " + finalizedValue);
            });
            return `{ ${propStrings.join(", ")} }`;
        }
        return "";
    }

    private buildLivePanel() {
        this.livePanel = new Panel({
            fillRow: true,
            padding: 8,
            backgroundColor: "defaultBackground",
            color: "defaultColor",
            borderWidth: 1,
            borderColor: "strokeSecondary",
            align: HorizontalAlignment.CENTER,
            borderRadius: 4,
            borderShadow: true,
            margin: 16,
            marginTop: 0
        });
        this.liveComponent = new this.exampleInput.componentClassRef(this.exampleInput.props);
        if (this.exampleInput.value !== undefined)
            this.liveComponent[this.exampleInput.name] = this.exampleInput.value;
        this.livePanel.add(this.liveComponent);
    }

    private doubleQuoteIfString(value: any): string {
        return (typeof value === "string") ? StringUtil.doubleQuote(value) : value;
    }

    private callOnCreateCallbacks() {
        this.exampleInput.onCreate.forEach(onCreateCallback => onCreateCallback(this));
    }
}
